import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useTranslation } from "react-i18next";
import { useNavigate, useSearchParams } from "react-router-dom";
import { debounce } from "lodash";
import { ArrowTopRightOnSquareIcon } from "@heroicons/react/24/outline";
import { toast } from "react-hot-toast";
import { CompactModuleFilter } from "../../utils/RelatedEntries/CompactModuleFilter";
import { useRelatedCollection } from "../../utils/RelatedEntries/useRelatedCollection";
import { RelatedEntries } from "../../utils/RelatedEntries/RelatedEntries";
import { RightDrawer } from "../../utils/PMTableView/RightDrawer";
import {
  mergeScopedFilters,
  useScopeFiltersSync,
  computeHasMore,
  embeddedFetchKey,
  scopeFiltersKey,
} from "../../utils/PMTableView/embedded-table-utils";
import { searchFieldsByName } from "../../Utils";
import {
  getCasesAsync,
  updateCaseAsync,
} from "../../../slices/case";
import { getUsersAsync } from "../../../slices/user";
import { fetchCaseTypes } from "../../../api/case/type";
import { priorityOptions } from "../quality/deviations/utils";
import {
  getCaseColumns,
  CASES_TABLE_FIELDS,
} from "./cases-table";
import { CaseDetailDrawer } from "./CaseDetailDrawer";
import CaseDialog from "./case-dialog";

export const CasesStackList = (props) => {
  const companyId = useSelector((state) => state.session.currentCompany?.id);
  return <CasesStackListContent key={JSON.stringify([companyId, props.module, props.filters])} {...props} />;
};

const CasesStackListContent = (props) => {
  const { filters: scopeFilters = [], module: moduleName = "embedded" } = props;
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [caseState, loadItems, patchItem] = useRelatedCollection(getCasesAsync, JSON.stringify(scopeFilters));
  const userState = useSelector((store) => store.users);
  const { isLoading, totalCount } = caseState;

  const loadMoreInFlightRef = useRef(false);
  const [drawerCase, setDrawerCase] = useState(null);
  const [caseTypes, setCaseTypes] = useState([]);
  const [caseDialogOpen, setCaseDialogOpen] = useState(false);
  const [selectedCase, setSelectedCase] = useState(null);

  const caseId = searchParams.get("caseId");

  const [controller, setController] = useState(() => ({
    filters: mergeScopedFilters(scopeFilters, []),
    page: 1,
    per_page: props.per_page ? parseInt(props.per_page, 10) : 50,
    query: "",
    sort: "desc",
    sortBy: "created_at",
    view: "all",
    fields: `${CASES_TABLE_FIELDS},project`,
  }));

  useScopeFiltersSync(scopeFilters, setController);

  useEffect(() => {
    if (caseId && caseId !== "null" && caseId !== "undefined") {
      navigate(`/dashboard/cases/${caseId}`);
    }
  }, [caseId, navigate]);

  useEffect(() => {
    dispatch(
      getUsersAsync({
        filters: [{ name: "role", value: "case" }],
        page: 1,
        per_page: 100,
        query: "",
        sort: "asc",
        sortBy: "name",
        view: "all",
        fields: "id,name,displayTitle",
      })
    );
    fetchCaseTypes()
      .then((response) => {
        setCaseTypes(response?.data?.data || response?.data || []);
      })
      .catch(() => setCaseTypes([]));
  }, [dispatch]);

  const userOptions = useMemo(
    () =>
      (userState.data || []).map((user) => ({
        value: user.id.toString(),
        label: user.displayTitle || user.name,
      })),
    [userState.data]
  );

  const handleCaseUpdate = useCallback(
    async (caseItem, patch) => {
      try {
        const updated = await dispatch(
          updateCaseAsync({ ...caseItem, ...patch })
        ).unwrap();
        if (updated?.id) {
          patchItem(updated);
          toast.success(t("Saved"));
          setDrawerCase((prev) =>
            prev && prev.id === updated.id ? { ...prev, ...updated } : prev
          );
          return updated;
        }
        toast.error(t("Could not save changes"));
        return null;
      } catch {
        toast.error(t("Could not save changes"));
        throw new Error("update failed");
      }
    },
    [dispatch, t, patchItem]
  );

  const columns = useMemo(
    () => getCaseColumns(t, { onUpdate: handleCaseUpdate, userOptions }),
    [t, handleCaseUpdate, userOptions]
  );
  const handleFiltersChange = (filters) => {
    const userFilters = [];

    if (filters.statuses?.length > 0) {
      userFilters.push({
        name: "status",
        value: JSON.stringify(filters.statuses),
      });
    }
    if (filters.users?.length > 0) {
      userFilters.push({
        name: "user_assigned_id",
        value: JSON.stringify(filters.users),
      });
    }
    if (filters.caseTypes?.length > 0) {
      userFilters.push({
        name: "case_type",
        value: JSON.stringify(filters.caseTypes),
      });
    }
    if (filters.priorities?.length > 0) {
      userFilters.push({
        name: "priority",
        value: JSON.stringify(filters.priorities),
      });
    }
    if (filters.onlyOverdue) {
      userFilters.push({
        name: "view",
        value: JSON.stringify(["ONLY_OVERDUE"]),
      });
    }

    setController((c) => ({
      ...c,
      page: 1,
      query: filters.query || "",
      filters: mergeScopedFilters(scopeFilters, userFilters),
    }));
  };

  const getInitialFilters = () => {
    const initial = {
      query: controller.query || "",
      statuses: [],
      users: [],
      caseTypes: [],
      priorities: [],
      onlyOverdue: false,
    };

    controller.filters.forEach((filter) => {
      if ((scopeFilters || []).some((sf) => sf.name === filter.name)) return;
      try {
        const values = JSON.parse(filter.value);
        switch (filter.name) {
          case "status":
            initial.statuses = Array.isArray(values) ? values : [values];
            break;
          case "user_assigned_id":
            initial.users = Array.isArray(values) ? values : [values];
            break;
          case "case_type":
            initial.caseTypes = Array.isArray(values) ? values : [values];
            break;
          case "priority":
            initial.priorities = Array.isArray(values) ? values : [values];
            break;
          case "view":
            initial.onlyOverdue =
              Array.isArray(values) && values.includes("ONLY_OVERDUE");
            break;
          default:
            break;
        }
      } catch {
        // ignore
      }
    });

    return initial;
  };

  const handleLoadMore = useCallback(() => {
    const loaded = caseState?.data?.length || 0;
    const total = totalCount || 0;
    if (loadMoreInFlightRef.current || isLoading || loaded >= total) return;

    const nextPage = Number(controller.page) + 1;
    loadMoreInFlightRef.current = true;
    loadItems({ ...controller, page: nextPage, append: true }).then((loaded) => { if (loaded) setController((c) => ({ ...c, page: nextPage })); }).finally(
      () => {
        loadMoreInFlightRef.current = false;
      }
    );
  }, [
    controller,
    loadItems,
    isLoading,
    caseState?.data?.length,
    totalCount,
  ]);

  const debouncedFetch = useMemo(
    () => debounce((ctrl) => loadItems(ctrl), 400),
    [loadItems]
  );

  const filtersKey = scopeFiltersKey(controller.filters);
  const fetchKey = useMemo(
    () => embeddedFetchKey(controller),
    [
      filtersKey,
      controller.query,
      controller.sort,
      controller.sortBy,
      controller.per_page,
      controller.view,
      controller.fields,
    ]
  );

  useEffect(() => {
    if (caseDialogOpen) return;
    setController((c) => (c.page === 1 ? c : { ...c, page: 1 }));
    debouncedFetch({ ...controller, page: 1 });
    return () => debouncedFetch.cancel();
  }, [fetchKey, debouncedFetch, caseDialogOpen]); // eslint-disable-line react-hooks/exhaustive-deps

  const dataLength = caseState?.data?.length || 0;
  const hasMore = computeHasMore({ isLoading, dataLength, totalCount });

  const caseCustomerName = (item) =>
    item?.customer?.id
      ? item?.customer?.displayTitle
      : item?.fields
      ? searchFieldsByName(item, "name")?.value
      : null;

  const caseTypeOptions = caseTypes.map((type) => ({
    value: type.id.toString(),
    label: type.displayTitle || type.title,
  }));

  return (
    <>
      <div className="min-w-0 space-y-4">
        <CompactModuleFilter
          disabled={isLoading}
          onFiltersChange={handleFiltersChange}
          initialFilters={getInitialFilters()}
          searchPlaceholder={t("Search cases (e.g., case number)...")}
          filterTitle={t("Filter Cases")}
          acceptedStatuses={["NEW", "PROCESSING", "FINISHED", "CLOSED"]}
          userOptions={userOptions}
          customDropdownFilters={[
            {
              key: "caseTypes",
              label: t("Case Types"),
              options: caseTypeOptions,
            },
            {
              key: "priorities",
              label: t("Priorities"),
              options: priorityOptions.map((priority, index) => ({
                value: (index + 1).toString(),
                label: priority.label,
              })),
            },
          ]}
          customCheckboxButtonFilters={[
            {
              key: "onlyOverdue",
              label: t("Only Overdue"),
              defaultValue: false,
            },
          ]}
        />

        <div className="min-w-0">
          <RelatedEntries kind="cases" context={moduleName} data={caseState.data} isLoading={isLoading} error={caseState.error} totalCount={totalCount}
          onRetry={() => { setController((value) => ({ ...value, page: 1 })); loadItems({ ...controller, page: 1 }); }}
          onRowClick={setDrawerCase} renderStatus={(item) => columns.find((column) => column.key === "status").cell(item)} hasMore={hasMore} onLoadMore={handleLoadMore}
        />
        </div>
      </div>

      <RightDrawer
        open={!!drawerCase}
        onClose={() => setDrawerCase(null)}
        eyebrow={drawerCase ? `${t("Case")} ${drawerCase.case_number || ""}` : ""}
        title={
          drawerCase
            ? caseCustomerName(drawerCase) || drawerCase.title || t("Cases")
            : t("Cases")
        }
        actions={[
          {
            label: t("Open"),
            icon: ArrowTopRightOnSquareIcon,
            primary: true,
            onClick: () => navigate(`/dashboard/cases/${drawerCase.id}`),
          },
        ]}
      >
        {drawerCase && (
          <CaseDetailDrawer
            caseItem={drawerCase}
            userOptions={userOptions}
            onUpdate={handleCaseUpdate}
          />
        )}
      </RightDrawer>

      <CaseDialog
        open={caseDialogOpen}
        onClose={() => {
          setCaseDialogOpen(false);
          setSelectedCase(null);
        }}
        selected={selectedCase}
        onRefresh={() => loadItems(controller)}
      />
    </>
  );
};

CasesStackList.defaultProps = {
  filters: [],
  sort: "desc",
  sortBy: "created_at",
};
